import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, CircleSlash, TriangleAlert, X } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * Shared settled-state ("receipt") model for interactive blocks.
 *
 * An interactive block (`suggestions`, `form`, `approval`, and any `carousel`
 * whose items carry actions) is a live control only until it is acted on. Once
 * acted on, it carries a durable `receipt` and re-renders in a settled "this is
 * what happened" state instead of staying a clickable control. This is the
 * tool-ui receipt convention (see `docs/rich-blocks-catalog.md` §4), adopted so
 * interaction is replay-safe: on a history reload an already-submitted block
 * renders its outcome rather than re-offering a live, un-actioned control.
 *
 * The shape is strictly additive to the K2 envelope - an optional
 * `props.receipt`. Display-only blocks never carry one.
 *
 *   { outcome: "success" | "partial" | "failed" | "cancelled",
 *     summary: string,
 *     identifiers?: Record<string, string | number>,
 *     at?: string }   // ISO-8601 timestamp
 *
 * Mirrored as Pydantic in the runtime (`app/blocks/`) per the registry
 * contract; this package validates the same shape defensively (no Zod dep).
 */

/** The four terminal outcomes an interactive block can settle into. */
export type BlockReceiptOutcome = "success" | "partial" | "failed" | "cancelled";

/** A durable record of how an interactive block was acted on. */
export interface BlockReceipt {
  /** Terminal outcome of the interaction. */
  outcome: BlockReceiptOutcome;
  /** One-line human summary of what happened (e.g. "Deployed to staging"). */
  summary: string;
  /**
   * Optional structured identifiers produced by the action (order id, run id,
   * the chosen option id, ...). Rendered as a compact key -> value strip.
   */
  identifiers?: Record<string, string | number>;
  /** ISO-8601 timestamp of when the interaction settled. */
  at?: string;
}

/**
 * Narrow an `unknown` payload (a streamed value that may be malformed) to a
 * `BlockReceipt`, or `undefined` if it is absent / the wrong shape. Used by
 * every interactive block's renderer to decide between "live control" and
 * "settled receipt" without throwing.
 */
export function readReceipt(value: unknown): BlockReceipt | undefined {
  if (!value || typeof value !== "object") return undefined;
  const rec = value as Record<string, unknown>;
  const outcome = rec.outcome;
  const summary = rec.summary;
  if (
    outcome !== "success" &&
    outcome !== "partial" &&
    outcome !== "failed" &&
    outcome !== "cancelled"
  ) {
    return undefined;
  }
  if (typeof summary !== "string") return undefined;

  const identifiers =
    rec.identifiers && typeof rec.identifiers === "object"
      ? Object.fromEntries(
          Object.entries(rec.identifiers as Record<string, unknown>).filter(
            ([, v]) => typeof v === "string" || typeof v === "number",
          ) as Array<[string, string | number]>,
        )
      : undefined;

  return {
    outcome,
    summary,
    identifiers: identifiers && Object.keys(identifiers).length > 0 ? identifiers : undefined,
    at: typeof rec.at === "string" ? rec.at : undefined,
  };
}

const receiptVariants = cva(
  cn("flex items-start gap-2.5 rounded-2xl border p-4 text-sm", "transition-colors"),
  {
    variants: {
      outcome: {
        success: "border-success/30 bg-success/5 text-foreground",
        partial: "border-warning/30 bg-warning/5 text-foreground",
        failed: "border-destructive/30 bg-destructive/5 text-foreground",
        cancelled: "border-border bg-muted/40 text-muted-foreground",
      },
    },
    defaultVariants: { outcome: "success" },
  },
);

const ICONS: Record<BlockReceiptOutcome, React.ComponentType<{ className?: string }>> = {
  success: Check,
  partial: TriangleAlert,
  failed: X,
  cancelled: CircleSlash,
};

const ICON_TONE: Record<BlockReceiptOutcome, string> = {
  success: "text-success",
  partial: "text-warning",
  failed: "text-destructive",
  cancelled: "text-muted-foreground",
};

const OUTCOME_LABEL: Record<BlockReceiptOutcome, string> = {
  success: "Done",
  partial: "Partially done",
  failed: "Failed",
  cancelled: "Cancelled",
};

/** Format an ISO timestamp for display; falls back to the raw string if unparseable. */
function formatAt(at: string | undefined): string | undefined {
  if (!at) return undefined;
  const t = Date.parse(at);
  if (Number.isNaN(t)) return at;
  // Locale read only at render on the client; deterministic on the server.
  return new Date(t).toLocaleString(
    typeof navigator !== "undefined" ? navigator.language : undefined,
    { dateStyle: "medium", timeStyle: "short" },
  );
}

export interface BlockReceiptViewProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof receiptVariants> {
  /** The settled receipt to render. */
  receipt: BlockReceipt;
}

/**
 * Renders an interactive block's settled state. Every interactive block (new +
 * the backfilled `form` / `approval`) delegates here when `props.receipt` is
 * present, so the settled look is identical across the palette.
 *
 * Token-themed throughout (`bg-success/5`, `text-destructive`, ...); a11y via
 * `role="status"` + an `sr-only` outcome word so screen readers announce the
 * terminal state. SSR-safe (timestamp formatting reads the locale only at
 * render, never `window`/`document`).
 */
export const BlockReceiptView = React.forwardRef<HTMLDivElement, BlockReceiptViewProps>(
  function BlockReceiptView({ receipt, className, ...rest }, ref) {
    const Icon = ICONS[receipt.outcome];
    const when = formatAt(receipt.at);
    const identifiers = receipt.identifiers ? Object.entries(receipt.identifiers) : [];

    return (
      <div
        ref={ref}
        role="status"
        className={cn(receiptVariants({ outcome: receipt.outcome }), className)}
        data-slot="block-receipt"
        data-outcome={receipt.outcome}
        {...rest}
      >
        <Icon className={cn("mt-0.5 size-4 shrink-0", ICON_TONE[receipt.outcome])} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-medium leading-snug">
            <span className="sr-only">{OUTCOME_LABEL[receipt.outcome]}: </span>
            {receipt.summary}
          </p>
          {identifiers.length > 0 ? (
            <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {identifiers.map(([key, value]) => (
                <div key={key} className="inline-flex items-baseline gap-1">
                  <dt className="font-medium">{key}</dt>
                  <dd className="font-mono tabular-nums text-foreground">{String(value)}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          {when ? (
            <time className="mt-1 block text-xs text-muted-foreground" dateTime={receipt.at}>
              {when}
            </time>
          ) : null}
        </div>
      </div>
    );
  },
);

BlockReceiptView.displayName = "BlockReceiptView";

export { receiptVariants };
export type BlockReceiptVariantProps = VariantProps<typeof receiptVariants>;
